import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { KeyStatsCard } from "./KeyStatsCard";
import { CircleOff, Plus, Redo2Icon, Settings } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { Tabs } from "@shared/ui/tabs";
import LastRunDetails from "./tabs/LastRunDetails";
import { BillCycleRunsTable } from "./tabs/BillCycleRunsTable";
import { BillCycleConsumerList } from "./tabs/BillCycleConsumerList";
import { PremiseTableWithActions } from "./tabs/PremiseTableWithActions";
import { useArchiveBillCycle, useBillCycleDetails } from "../hooks";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { AddPremiseToCycle } from "./AddPremiseToCycle";
import { useState } from "react";

// ============= EXTRACTED COMPONENTS =============
const SectionBlock = ({ title, children }) => (
  <div className="space-y-2 p-2 border rounded-md">
    <h3 className="text-sm font-semibold tracking-wide">{title}</h3>
    {children}
  </div>
);

const GridBlock = ({ items }) => (
  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
    {items.map((item) => (
      <ValueLabel
        key={item.label}
        label={item.label}
        value={item.value}
        tooltip={item.tip}
        description={item.description}
        layout="vertical"
        className=""
      />
    ))}
  </div>
);

// ============= CONFIGURATION CONSTANTS =============
const BILL_STATISTICS_CONFIG = [
  { key: "totalBills", label: "Total Bills", colorClass: "text-gray-900" },
  {
    key: "actualBillsCount",
    label: "Actual Bills",
    colorClass: "text-green-600",
  },
  {
    key: "estimatedBillsCount",
    label: "Estimated Bills",
    colorClass: "text-yellow-600",
  },
];

const QUICK_ACTIONS_CONFIG = [
  {
    label: "Generate Bills",
    icon: Redo2Icon,
    action: "GENERATE_BILLS",
    className: "",
  },
  {
    label: "Edit Bill Cycle",
    icon: Settings,
    action: "EDIT_CYCLE",
    className: "",
  },
  {
    label: "Add Premise",
    icon: Plus,
    action: "ADD_PREMISE",
    className: "",
  },
  {
    label: "Archive Cycle",
    icon: CircleOff,
    action: "ARCHIVE_CYCLE",
    className: "border-red-500 bg-white text-red-500",
  },
];

// ============= MAIN COMPONENT =============
function BillCycleDetail() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const billCycleId = params?.id ? parseInt(params.id) : 0;

  const {
    data: billCycleDetails,
    isLoading: isLoadingDetails,
    refetch,
  } = useBillCycleDetails(billCycleId);
  const { mutate: archiveCycle } = useArchiveBillCycle(params?.id);

  // ============= ACTION HANDLERS MAP =============
  const actionHandlers = {
    GENERATE_BILLS: () => navigate(`/bx/create-bill-generation`),
    EDIT_CYCLE: () =>
      navigate(`/bx/bill-setup/billcycle/action/edit/${billCycleDetails?.id}`),
    ADD_PREMISE: () => setIsAddDialogOpen(true),
    ARCHIVE_CYCLE: () => {
      archiveCycle(params?.id, {
        onSuccess: () => refetch(),
      });
    },
  };

  const handleQuickAction = (action) => {
    const handler = actionHandlers[action];
    if (handler) handler();
  };

  // ============= TAB CONFIGURATION =============
  const tabComponents = {
    "last-run": {
      label: "Last Run Details",
      component: <LastRunDetails />,
    },
    "run-history": {
      label: "Run History",
      component: <BillCycleRunsTable />,
    },
    premises: {
      label: "Premises",
      component: (
        <PremiseTableWithActions
          premises={billCycleDetails?.premise}
          selectedCategories={billCycleDetails?.category}
          selectedSubCategories={billCycleDetails?.subCategory}
          refetchDetail={refetch}
          canPerformAction={
            billCycleDetails?.statusDisplay?.toLowerCase() !== "archive"
          }
        />
      ),
    },
    bills: {
      label: "Bills",
      component: <BillCycleConsumerList />,
    },
  };

  // ============= SECTION DATA CONFIGURATIONS =============
  const getCycleDetailsItems = () => [
    {
      label: "Billing Period",
      value: `${billCycleDetails?.invoiceStartDate || "NA"} - ${billCycleDetails?.invoiceEndDate || "NA"}`,
    },
    {
      label: "Category",
      value: (
        <HoverRevealText text={billCycleDetails?.categoryName?.join(", ")} />
      ),
    },
    {
      label: "Sub Category",
      value: (
        <HoverRevealText text={billCycleDetails?.subCategoryName?.join(", ")} />
      ),
    },
    {
      label: "Bill Distribution Days",
      value: billCycleDetails?.cycleDuration ?? "NA",
    },
    {
      label: "Premise",
      value: billCycleDetails?.totalPremiseCount ?? "NA",
    },
    {
      label: "Consumer",
      value: billCycleDetails?.consumerCount ?? "NA",
    },
    {
      label: "Meter",
      value: billCycleDetails?.totalMeterCount ?? "NA",
    },
  ];

  const getLastRunDetailsItems = () => [
    {
      label: "Last Run By",
      value: billCycleDetails?.lastRunBy ?? "NA",
    },
    {
      label: "Last Run Date",
      value: billCycleDetails?.lastRunDate ?? "NA",
    },
    {
      label: "Last Run Time",
      value: billCycleDetails?.lastRunTime ?? "NA",
    },
    {
      label: "Days Since Last Run",
      value: billCycleDetails?.daysSinceLastReading ?? "NA",
    },
  ];

  const getAuditInformationItems = () => [
    {
      label: "Created By",
      value: billCycleDetails?.createdUserRemoteName ?? "NA",
    },
    {
      label: "Created On",
      value: billCycleDetails?.createdDate ?? "NA",
    },
    {
      label: "Last Updated By",
      value: billCycleDetails?.lastModifiedUserRemoteName ?? "NA",
    },
    {
      label: "Last Updated On",
      value: billCycleDetails?.lastModifiedDate ?? "NA",
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col lg:flex-row">
        {/* Left Column */}
        <div className="mr-4 w-full lg:w-[70%] space-y-6">
          <Card className="overflow-hidden">
            {/* ================= HEADER ================= */}
            <div className="pr-4">
              <CardHeader className="flex flex-row items-center justify-between pb-4 border-none">
                <div className="flex items-start gap-4">
                  <div>
                    <CardTitle className="text-2xl tracking-tight">
                      {billCycleDetails?.billCycleName}
                    </CardTitle>
                  </div>
                  <Badge className="mt-1">
                    {billCycleDetails?.statusDisplay}
                  </Badge>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-4 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
                    {BILL_STATISTICS_CONFIG.map((stat, index) => (
                      <div key={stat.key} className="flex items-center gap-4">
                        {index > 0 && (
                          <div className="h-8 w-px bg-gray-300"></div>
                        )}
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider">
                            {stat.label}
                          </p>
                          <p className={`text-lg font-bold ${stat.colorClass}`}>
                            {billCycleDetails?.billStatistics?.[stat.key] ?? 0}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardHeader>
            </div>

            {/* ================= BODY CONTENT ================= */}
            <CardContent className="pt-4 space-y-2 border-none">
              <SectionBlock title="Cycle Details">
                <GridBlock items={getCycleDetailsItems()} />
              </SectionBlock>

              <SectionBlock title="Last Run Details">
                <GridBlock items={getLastRunDetailsItems()} />
              </SectionBlock>

              <SectionBlock title="Audit Information">
                <GridBlock items={getAuditInformationItems()} />
              </SectionBlock>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="lg:w-[30%] space-y-4">
          <KeyStatsCard
            data={{
              consumerCount: billCycleDetails?.consumerCount ?? 0,
              premiseCount: billCycleDetails?.totalPremiseCount ?? 0,
              runCount: billCycleDetails?.totalRunCount ?? 0,
              coverage: billCycleDetails?.consumerCoveragePercentage ?? 0,
            }}
          />

          {/* Quick Actions */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-start gap-4">
              <CardTitle className="text-base">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {QUICK_ACTIONS_CONFIG.map((action) => (
                <div
                  key={action.label}
                  className="flex items-center justify-between w-full"
                >
                  <Button
                    variant="outline"
                    className={`${action.className} w-full justify-center text-center disabled:cursor-not-allowed`}
                    onClick={() => handleQuickAction(action.action)}
                    disabled={
                      billCycleDetails?.statusDisplay?.toLowerCase() ===
                      "archive"
                    }
                  >
                    <action.icon className="h-4 w-4 mr-2" />
                    {action.label}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      <Tabs
        defaultValue="last-run"
        tabComponents={tabComponents}
        tabsListClassName="grid grid-cols-4 gap-2"
        className="space-y-4"
      />

      <AddPremiseToCycle
        premises={billCycleDetails?.premises}
        selectedCategories={billCycleDetails?.category}
        selectedSubCategories={billCycleDetails?.subCategory}
        isDialogOpen={isAddDialogOpen}
        setIsDialogOpen={() => setIsAddDialogOpen(false)}
        onConfirm={refetch}
      />
    </div>
  );
}

export default BillCycleDetail;
